<template>
  <div v-if="enabled" class="wisk-ai-chat">
    <div v-if="messages.length" class="wisk-ai-chat-toolbar">
      <button type="button" class="wisk-ai-chat-new" @click="newConversation">+ New chat</button>
    </div>

    <div ref="feed" class="wisk-ai-chat-feed">
      <div v-if="!messages.length" class="wisk-ai-chat-empty">Ask me anything about {{ venueName || 'your venue' }} — inventory, variance, sales, purchasing, or costs.</div>

      <div v-for="(msg, index) in messages" :key="index" class="wisk-ai-chat-message" :class="`wisk-ai-chat-message--${msg.role}`">
        <div v-if="msg.role === 'assistant'" class="wisk-ai-chat-assistant">
          <div v-if="msg.text" class="wisk-ai-chat-bubble wisk-ai-chat-markdown" v-html="renderMarkdown(msg.text)"></div>
          <div v-if="msg.error" class="wisk-ai-chat-bubble wisk-ai-chat-bubble--error">{{ msg.error }}</div>

          <div v-if="canRate(index)" class="wisk-ai-chat-feedback">
            <template v-if="msg.feedback.sent">
              <span class="wisk-ai-chat-feedback-thanks">Thanks, your feedback was sent.</span>
            </template>
            <template v-else>
              <button
                type="button"
                class="wisk-ai-chat-feedback-btn"
                :class="{ 'wisk-ai-chat-feedback-btn--active': msg.feedback.rating === 'up' }"
                :disabled="msg.feedback.sending"
                title="Good answer"
                aria-label="Good answer"
                @click="rateUp(msg, index)">
                <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                  <path
                    fill="currentColor"
                    d="M2 21h4V9H2v12Zm20-11a2 2 0 0 0-2-2h-6.31l.95-4.57.03-.32a1.5 1.5 0 0 0-.44-1.06L13.17 1 6.59 7.59A2 2 0 0 0 6 9v10a2 2 0 0 0 2 2h9a2 2 0 0 0 1.84-1.22l3.02-7.05A2 2 0 0 0 22 12v-2Z" />
                </svg>
              </button>
              <button
                type="button"
                class="wisk-ai-chat-feedback-btn"
                :class="{ 'wisk-ai-chat-feedback-btn--active': msg.feedback.open }"
                :disabled="msg.feedback.sending"
                title="Report an issue"
                aria-label="Report an issue"
                @click="toggleReport(msg)">
                <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                  <path
                    fill="currentColor"
                    d="M22 3h-4v12h4V3ZM2 14a2 2 0 0 0 2 2h6.31l-.95 4.57-.03.32c0 .41.17.79.44 1.06L10.83 23l6.58-6.59A2 2 0 0 0 18 15V5a2 2 0 0 0-2-2H7a2 2 0 0 0-1.84 1.22L2.14 11.27A2 2 0 0 0 2 12v2Z" />
                </svg>
              </button>
            </template>
          </div>

          <form v-if="msg.feedback.open && !msg.feedback.sent" class="wisk-ai-chat-report" @submit.prevent="submitReport(msg, index)">
            <div class="wisk-ai-chat-report-title">What went wrong?</div>
            <div class="wisk-ai-chat-report-categories">
              <button
                v-for="category in feedbackCategories"
                :key="category.id"
                type="button"
                class="wisk-ai-chat-report-category"
                :class="{ 'wisk-ai-chat-report-category--active': msg.feedback.category === category.id }"
                @click="msg.feedback.category = msg.feedback.category === category.id ? null : category.id">
                {{ category.label }}
              </button>
            </div>
            <textarea v-model="msg.feedback.comment" rows="2" maxlength="4000" placeholder="Tell us more (optional)"></textarea>
            <div v-if="msg.feedback.error" class="wisk-ai-chat-error">{{ msg.feedback.error }}</div>
            <div class="wisk-ai-chat-report-actions">
              <button type="button" class="wisk-ai-chat-report-cancel" :disabled="msg.feedback.sending" @click="msg.feedback.open = false">Cancel</button>
              <button type="submit" :disabled="msg.feedback.sending">{{ msg.feedback.sending ? 'Sending…' : 'Send' }}</button>
            </div>
          </form>
          <div v-else-if="msg.feedback.error && !msg.feedback.sent" class="wisk-ai-chat-error">{{ msg.feedback.error }}</div>
        </div>
        <div v-else class="wisk-ai-chat-bubble">{{ msg.text }}</div>
      </div>

      <div v-if="activeTool" class="wisk-ai-chat-status">Looking up {{ activeTool }}…</div>
      <div v-else-if="loading" class="wisk-ai-chat-status">Thinking…</div>
      <div v-if="error" class="wisk-ai-chat-error">{{ error }}</div>
    </div>

    <div v-if="capReached" class="wisk-ai-chat-cap wisk-ai-chat-cap--reached">
      {{ translations.txtAiChatAllowanceReached }}
      <template v-if="resetDate">{{ translations.translate('tplAiChatAllowanceResetsOn', { '{a}': resetDate }) }}</template>
    </div>
    <div v-else-if="usageWarning" class="wisk-ai-chat-cap">
      {{ translations.translate('tplAiChatAllowanceWarning', { '{a}': usage.percent_used }) }}
      <template v-if="resetDate">{{ translations.translate('tplAiChatAllowanceResetsOn', { '{a}': resetDate }) }}</template>
    </div>

    <form class="wisk-ai-chat-input" @submit.prevent="onSubmit">
      <textarea ref="composer" v-model="input" rows="1" :placeholder="inputPlaceholder" :disabled="loading || transcribing || capReached" @input="autoGrow" @keydown="onComposerKeydown"></textarea>
      <button
        v-if="voiceSupported && !loading && !capReached"
        type="button"
        class="wisk-ai-chat-mic"
        :class="{ 'wisk-ai-chat-mic--recording': recording }"
        :disabled="transcribing"
        :title="recording ? 'Tap to finish and insert text' : 'Record voice'"
        @click="toggleVoice">
        <svg v-if="!recording" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
          <path fill="currentColor" d="M12 15a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v6a3 3 0 0 0 3 3Z" />
          <path fill="currentColor" d="M17 12a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21h2v-2.08A7 7 0 0 0 19 12h-2Z" />
        </svg>
        <svg v-else viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
          <path fill="currentColor" d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
        </svg>
      </button>
      <button v-if="loading" type="button" class="wisk-ai-chat-stop" @click="stop">Stop</button>
      <button v-else type="submit" :disabled="!input.trim() || transcribing || capReached">Send</button>
    </form>
  </div>
</template>

<script>
import { streamChat, sendChatFeedback, isAiChatEnabled, renderMarkdown, fetchAiChatUsage, formatAiChatResetDate, AI_CHAT_CAP_REACHED, FEEDBACK_CATEGORIES } from '@/modules/aiChat'
import { createVoiceRecorder, isVoiceInputSupported } from '@/modules/voiceInput'
import api from '@/api'

export default {
  name: 'WiskAiChat',
  data() {
    return {
      messages: [],
      input: '',
      loading: false,
      activeTool: null,
      error: null,
      controller: null,
      recorder: null,
      recording: false,
      transcribing: false,
      composerObserver: null,
      composerWidth: 0,
      usage: null,
      feedbackCategories: FEEDBACK_CATEGORIES
    }
  },
  computed: {
    enabled() {
      return isAiChatEnabled(this.$store.state.user)
    },
    voiceSupported() {
      return isVoiceInputSupported()
    },
    inputPlaceholder() {
      if (this.recording) return 'Listening… tap ✓ when done'
      if (this.transcribing) return 'Transcribing…'
      return 'Ask about your venue…'
    },
    venueName() {
      return this.$store.state.venue?.title || ''
    },
    venueContext() {
      const venue = this.$store.state.venue
      return venue?.id ? { id: venue.id, title: venue.title } : undefined
    },
    venueId() {
      return this.$store.state.venue?.id
    },
    translations() {
      return this.$store.state.translations
    },
    capReached() {
      return !!this.usage?.cap_reached
    },
    usageWarning() {
      return !!this.usage?.warning && !this.capReached
    },
    resetDate() {
      return formatAiChatResetDate(this.usage?.resets_at, this.$store.state.user?.language)
    }
  },
  methods: {
    renderMarkdown,
    async refreshUsage() {
      if (!this.enabled) return
      const venueId = this.venueId
      const usage = await fetchAiChatUsage(venueId)
      // A slow response for a venue the user has since left must not overwrite the current one.
      if (usage && venueId === this.venueId) this.usage = usage
    },
    onSubmit() {
      this.sendMessage(this.input)
    },
    onComposerKeydown(event) {
      if (event.key !== 'Enter' || event.isComposing) return

      // Alt/Option + Enter has no native effect in a textarea, so the line break is inserted by hand.
      if (event.altKey) {
        event.preventDefault()
        this.insertNewLine()
      } else if (!event.shiftKey) {
        event.preventDefault()
        this.onSubmit()
      }
    },
    insertNewLine() {
      const composer = this.$refs.composer
      if (!composer) return

      // execCommand keeps the browser's own undo stack, which rewriting the value would discard.
      if (document.execCommand('insertText', false, '\n')) {
        this.$nextTick(this.autoGrow)
        return
      }

      const { selectionStart, selectionEnd } = composer
      this.input = `${this.input.slice(0, selectionStart)}\n${this.input.slice(selectionEnd)}`

      this.$nextTick(() => {
        composer.selectionStart = selectionStart + 1
        composer.selectionEnd = selectionStart + 1
        this.autoGrow()
      })
    },
    autoGrow() {
      const composer = this.$refs.composer
      if (!composer) return
      composer.style.height = 'auto'
      composer.style.height = `${composer.scrollHeight}px`
    },
    // A turn that failed without any answer stays on screen (so it can be reported) but is left out
    // of the history, along with the question that produced it, so the roles keep alternating.
    historyBefore(index) {
      const history = []
      this.messages.slice(0, index).forEach((m, i, list) => {
        if (m.role === 'assistant' && !m.text) return
        if (m.role === 'user' && list[i + 1] && !list[i + 1].text) return
        history.push({ role: m.role, text: m.text })
      })
      return history
    },
    newFeedbackState() {
      return { rating: null, open: false, category: null, comment: '', sending: false, sent: false, error: null }
    },
    canRate(index) {
      const msg = this.messages[index]
      const streaming = this.loading && index === this.messages.length - 1
      return !streaming && (msg.text || msg.error)
    },
    rateUp(msg, index) {
      msg.feedback.open = false
      this.submitFeedback(msg, index, 'up')
    },
    toggleReport(msg) {
      msg.feedback.error = null
      msg.feedback.open = !msg.feedback.open
      if (msg.feedback.open && msg.error && !msg.feedback.category) msg.feedback.category = 'error'
      this.scrollToBottom()
    },
    submitReport(msg, index) {
      this.submitFeedback(msg, index, 'down')
    },
    async submitFeedback(msg, index, rating) {
      if (msg.feedback.sending) return
      const question = this.messages[index - 1]
      msg.feedback.sending = true
      msg.feedback.error = null
      msg.feedback.rating = rating
      try {
        await sendChatFeedback({
          venue: msg.venue || this.venueContext,
          rating,
          category: rating === 'down' ? msg.feedback.category || undefined : undefined,
          comment: rating === 'down' ? msg.feedback.comment.trim() || undefined : undefined,
          turnId: msg.turnId || undefined,
          message: question?.role === 'user' ? question.text : '',
          response: msg.text,
          error: msg.error || undefined,
          history: this.historyBefore(index - 1),
          toolNames: msg.tools
        })
        msg.feedback.sent = true
        msg.feedback.open = false
      } catch {
        msg.feedback.rating = null
        msg.feedback.error = 'Could not send your feedback. Please try again.'
      } finally {
        msg.feedback.sending = false
      }
    },
    async sendMessage(text) {
      const message = (text || '').trim()
      if (!message || this.loading) return

      const history = this.historyBefore(this.messages.length)
      const venue = this.venueContext
      this.messages.push({ role: 'user', text: message })
      this.messages.push({ role: 'assistant', text: '', error: null, turnId: null, tools: [], venue, feedback: this.newFeedbackState() })
      const assistant = this.messages[this.messages.length - 1]

      this.input = ''
      this.$nextTick(this.autoGrow)
      this.loading = true
      this.error = null
      this.controller = new AbortController()
      this.scrollToBottom()

      try {
        await streamChat({
          message,
          history,
          venue,
          signal: this.controller.signal,
          onTurn: turnId => {
            assistant.turnId = turnId
          },
          onText: chunk => {
            assistant.text += chunk
            this.scrollToBottom()
          },
          onTool: name => {
            this.activeTool = name
            assistant.tools.push(name)
          }
        })
      } catch (err) {
        if (err?.name === 'AbortError') {
          if (!assistant.text) this.messages.pop()
        } else if (err?.code === AI_CHAT_CAP_REACHED) {
          // Not an answer that failed, so nothing to report: take the turn back off the feed and
          // return the question to the composer for when the allowance resets.
          this.messages.splice(this.messages.length - 2, 2)
          this.input = message
          this.$nextTick(this.autoGrow)
          if (err.usage) this.usage = err.usage
        } else {
          // Kept on the answer itself rather than as a banner, so a failed turn can be reported.
          assistant.error = err?.message || 'Something went wrong.'
        }
      } finally {
        this.loading = false
        this.activeTool = null
        this.controller = null
        this.scrollToBottom()
        this.refreshUsage()
      }
    },
    stop() {
      this.controller?.abort()
    },
    async toggleVoice() {
      if (this.recording) {
        await this.stopVoice()
      } else {
        await this.startVoice()
      }
    },
    async startVoice() {
      this.error = null
      this.recorder = createVoiceRecorder()
      try {
        await this.recorder.start()
        this.recording = true
      } catch (err) {
        this.recorder = null
        this.error = err?.message || 'Could not access the microphone.'
      }
    },
    async stopVoice() {
      if (!this.recorder) return
      this.recording = false
      this.transcribing = true
      try {
        const file = await this.recorder.stop()
        const formData = new FormData()
        formData.append('file', file)
        const { data } = await api.transcribeAudio(formData)
        const transcript = (data?.transcript || '').trim()
        if (transcript) {
          this.input = this.input ? `${this.input} ${transcript}`.trim() : transcript
          this.$nextTick(this.autoGrow)
        } else {
          this.error = 'No speech detected. Please try again.'
        }
      } catch (err) {
        this.error = err?.response?.data?.message || err?.message || 'Could not transcribe the audio.'
      } finally {
        this.transcribing = false
        this.recorder = null
      }
    },
    cancelVoice() {
      if (this.recorder) {
        this.recorder.cancel()
        this.recorder = null
      }
      this.recording = false
      this.transcribing = false
    },
    newConversation() {
      this.stop()
      this.cancelVoice()
      this.messages = []
      this.input = ''
      this.$nextTick(this.autoGrow)
      this.error = null
      this.activeTool = null
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const feed = this.$refs.feed
        if (feed) feed.scrollTop = feed.scrollHeight
      })
    }
  },
  mounted() {
    this.autoGrow()
    this.refreshUsage()

    if (this.enabled && typeof ResizeObserver !== 'undefined') {
      this.composerObserver = new ResizeObserver(entries => {
        const width = entries[0]?.contentRect?.width || 0
        if (width && width !== this.composerWidth) {
          this.composerWidth = width
          this.autoGrow()
        }
      })
      this.composerObserver.observe(this.$el)
    }
  },
  beforeUnmount() {
    this.cancelVoice()
    this.composerObserver?.disconnect()
    this.composerObserver = null
  },
  watch: {
    enabled(value) {
      if (value) this.refreshUsage()
    },
    venueId() {
      this.usage = null
      this.refreshUsage()
    }
  }
}
</script>

<style lang="scss" scoped>
.wisk-ai-chat {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 320px;
}

.wisk-ai-chat-toolbar {
  display: flex;
  justify-content: flex-end;
  padding: 4px 8px 0;
}

.wisk-ai-chat-new {
  border: none;
  background: transparent;
  color: var(--wisk-primary, #2f6fed);
  font-size: 12px;
  cursor: pointer;
}

.wisk-ai-chat-feed {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
}

.wisk-ai-chat-empty {
  font-size: 13px;
  opacity: 0.7;
  padding: 8px;
}

.wisk-ai-chat-message {
  display: flex;
  margin: 6px 0;

  &--user {
    justify-content: flex-end;
  }
}

.wisk-ai-chat-bubble {
  max-width: 85%;
  min-width: 0;
  padding: 8px 12px;
  border-radius: 12px;
  background: var(--wisk-background-alt, #f1f1f4);

  .wisk-ai-chat-message--user & {
    background: var(--wisk-primary, #2f6fed);
    color: #fff;
    white-space: pre-wrap;
  }
}

.wisk-ai-chat-markdown {
  :deep(p) {
    margin: 0 0 8px;
  }
  :deep(p:last-child) {
    margin-bottom: 0;
  }
  :deep(ul),
  :deep(ol) {
    margin: 4px 0;
    padding-left: 20px;
  }
  :deep(table) {
    display: block;
    max-width: 100%;
    overflow-x: auto;
    border-collapse: collapse;
    margin: 6px 0;
    font-size: 13px;
  }
  :deep(th),
  :deep(td) {
    border: 1px solid var(--wisk-border, #e0e0e6);
    padding: 4px 8px;
    text-align: left;
  }
  :deep(code) {
    background: var(--wisk-code-background, rgba(0, 0, 0, 0.06));
    padding: 1px 4px;
    border-radius: 4px;
    font-size: 12px;
  }
  :deep(pre) {
    background: var(--wisk-code-background, rgba(0, 0, 0, 0.06));
    padding: 8px;
    border-radius: 6px;
    overflow-x: auto;
  }
}

.wisk-ai-chat-assistant {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 85%;
  min-width: 0;

  .wisk-ai-chat-bubble {
    max-width: 100%;
  }
}

.wisk-ai-chat-bubble--error {
  color: var(--wisk-danger, #d64545);
  font-size: 13px;
  margin-top: 4px;
}

.wisk-ai-chat-feedback {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-top: 2px;
}

.wisk-ai-chat-feedback-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: inherit;
  opacity: 0.45;
  cursor: pointer;

  &:hover:not(:disabled),
  &--active {
    opacity: 1;
    color: var(--wisk-primary, #2f6fed);
  }

  &:disabled {
    cursor: default;
  }
}

.wisk-ai-chat-feedback-thanks {
  font-size: 12px;
  opacity: 0.6;
  padding: 4px 2px;
}

.wisk-ai-chat-report {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  min-width: 240px;
  margin-top: 4px;
  padding: 8px;
  border: 1px solid var(--wisk-border, #e0e0e6);
  border-radius: 8px;

  textarea {
    width: 100%;
    padding: 6px 8px;
    border: 1px solid var(--wisk-border, #e0e0e6);
    border-radius: 6px;
    font: inherit;
    font-size: 13px;
    resize: vertical;
    background: transparent;
    color: inherit;
  }
}

.wisk-ai-chat-report-title {
  font-size: 12px;
  font-weight: 600;
}

.wisk-ai-chat-report-categories {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.wisk-ai-chat-report-category {
  padding: 3px 8px;
  border: 1px solid var(--wisk-border, #e0e0e6);
  border-radius: 12px;
  background: transparent;
  color: inherit;
  font-size: 12px;
  cursor: pointer;

  &--active {
    border-color: var(--wisk-primary, #2f6fed);
    background: var(--wisk-primary, #2f6fed);
    color: #fff;
  }
}

.wisk-ai-chat-report-actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;

  button {
    padding: 4px 12px;
    border: none;
    border-radius: 6px;
    background: var(--wisk-primary, #2f6fed);
    color: #fff;
    font-size: 12px;
    cursor: pointer;

    &:disabled {
      opacity: 0.5;
      cursor: default;
    }
  }

  .wisk-ai-chat-report-cancel {
    background: transparent;
    color: inherit;
    border: 1px solid var(--wisk-border, #e0e0e6);
  }
}

.wisk-ai-chat-status {
  font-size: 12px;
  opacity: 0.6;
  padding: 4px 8px;
}

.wisk-ai-chat-error {
  font-size: 12px;
  color: var(--wisk-danger, #d64545);
  padding: 4px 8px;
}

.wisk-ai-chat-cap {
  margin: 0 8px 4px;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 12px;
  background: var(--wisk-warning-background, #fff4e0);
  color: var(--wisk-warning-text, #8a5a00);

  &--reached {
    background: var(--wisk-danger-background, #fdecec);
    color: var(--wisk-danger, #d64545);
  }
}

.wisk-ai-chat-input {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 8px;
  border-top: 1px solid var(--wisk-border, #e0e0e6);

  textarea {
    flex: 1;
    min-width: 0;
    padding: 8px 10px;
    border: 1px solid var(--wisk-border, #e0e0e6);
    border-radius: 8px;
    background: var(--wisk-background, #fff);
    color: inherit;
    font: inherit;
    line-height: 1.4;
    resize: none;
    overflow-y: auto;
    min-height: 76px;
    max-height: 40vh;
  }

  button {
    padding: 8px 16px;
    border: none;
    border-radius: 8px;
    background: var(--wisk-primary, #2f6fed);
    color: #fff;
    cursor: pointer;

    &:disabled {
      opacity: 0.5;
      cursor: default;
    }
  }

  .wisk-ai-chat-stop {
    background: var(--wisk-danger, #d64545);
  }

  .wisk-ai-chat-mic {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 9px 12px;
    background: transparent;
    border: 1px solid var(--wisk-border, #e0e0e6);
    color: var(--wisk-primary, #2f6fed);

    &--recording {
      background: var(--wisk-primary, #2f6fed);
      border-color: var(--wisk-primary, #2f6fed);
      color: #fff;
      animation: wisk-ai-chat-mic-pulse 1.2s ease-in-out infinite;
    }
  }
}

@keyframes wisk-ai-chat-mic-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.55;
  }
}
</style>
